import React, { createContext, useContext, useState, useEffect } from 'react';
import { SponsorMetadata, SponsorMessageTemplate, TournamentSponsor } from '../types/sponsor';
import { supabase } from '../lib/supabase';
import { chatService } from '../lib/chat/chatService';

interface SponsorContextType {
  currentSponsor: TournamentSponsor | null;
  messageTemplates: SponsorMessageTemplate[];
  isLoading: boolean;
  error: string | null;
  updateSponsor: (sponsor: TournamentSponsor) => Promise<void>;
  createMessageTemplate: (template: Omit<SponsorMessageTemplate, 'id' | 'created_at'>) => Promise<void>;
  updateMessageTemplate: (template: SponsorMessageTemplate) => Promise<void>;
  deleteMessageTemplate: (id: string) => Promise<void>;
}

const SponsorContext = createContext<SponsorContextType | undefined>(undefined);

export function SponsorProvider({ children }: { children: React.ReactNode }) {
  const [currentSponsor, setCurrentSponsor] = useState<TournamentSponsor | null>(null);
  const [messageTemplates, setMessageTemplates] = useState<SponsorMessageTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load sponsor data
  useEffect(() => {
    const loadSponsorData = async () => {
      if (!supabase) return;

      try {
        // Get message templates
        const { data: templates, error: templatesError } = await supabase
          .from('sponsor_message_templates')
          .select('*')
          .order('created_at', { ascending: true });

        if (templatesError) throw templatesError;

        setMessageTemplates(templates || []);
        setError(null);
      } catch (err) {
        console.error('Error loading sponsor data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load sponsor data');
      } finally {
        setIsLoading(false);
      }
    };

    loadSponsorData();
  }, []);

  const updateSponsor = async (sponsor: TournamentSponsor) => {
    if (!supabase) return;

    try {
      const { error } = await supabase
        .from('tournament_sponsors')
        .upsert({
          tournament_id: sponsor.tournament_id,
          sponsor_metadata: sponsor.sponsor_metadata,
          start_time: sponsor.start_time,
          end_time: sponsor.end_time,
          message_template_id: sponsor.message_template_id,
          active: sponsor.active
        });

      if (error) throw error;

      setCurrentSponsor(sponsor);
      
      // Update chat service with new sponsor
      const template = messageTemplates.find(t => t.id === sponsor.message_template_id);
      chatService.setCurrentSponsor(sponsor, template);
      
      setError(null);
    } catch (err) {
      console.error('Error updating sponsor:', err);
      setError(err instanceof Error ? err.message : 'Failed to update sponsor');
      throw err;
    }
  };

  const createMessageTemplate = async (template: Omit<SponsorMessageTemplate, 'id' | 'created_at'>) => {
    if (!supabase) return;

    try {
      const { data, error } = await supabase
        .from('sponsor_message_templates')
        .insert({
          message: template.message,
          interval_minutes: template.interval_minutes,
          active: template.active
        })
        .select()
        .single();

      if (error) throw error;

      setMessageTemplates(prev => [...prev, data]);
      
      // Update chat service if this is the current template
      if (currentSponsor?.message_template_id === data.id) {
        chatService.setCurrentSponsor(currentSponsor, data);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error creating message template:', err);
      setError(err instanceof Error ? err.message : 'Failed to create message template');
      throw err;
    }
  };

  const updateMessageTemplate = async (template: SponsorMessageTemplate) => {
    if (!supabase) return;

    try {
      const { error } = await supabase
        .from('sponsor_message_templates')
        .update({
          message: template.message,
          interval_minutes: template.interval_minutes,
          active: template.active
        })
        .eq('id', template.id);

      if (error) throw error;

      setMessageTemplates(prev => 
        prev.map(t => t.id === template.id ? template : t)
      );
      
      // Update chat service if this is the current template
      if (currentSponsor?.message_template_id === template.id) {
        chatService.setCurrentSponsor(currentSponsor, template);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error updating message template:', err);
      setError(err instanceof Error ? err.message : 'Failed to update message template');
      throw err;
    }
  };

  const deleteMessageTemplate = async (id: string) => {
    if (!supabase) return;

    try {
      const { error } = await supabase
        .from('sponsor_message_templates')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setMessageTemplates(prev => prev.filter(t => t.id !== id));
      
      // Clear template from chat service if this was the current one
      if (currentSponsor?.message_template_id === id) {
        chatService.setCurrentSponsor(currentSponsor, null);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error deleting message template:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete message template');
      throw err;
    }
  };

  return (
    <SponsorContext.Provider value={{
      currentSponsor,
      messageTemplates,
      isLoading,
      error,
      updateSponsor,
      createMessageTemplate,
      updateMessageTemplate,
      deleteMessageTemplate
    }}>
      {children}
    </SponsorContext.Provider>
  );
}

export function useSponsor() {
  const context = useContext(SponsorContext);
  if (context === undefined) {
    throw new Error('useSponsor must be used within a SponsorProvider');
  }
  return context;
}